import { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FilePlus2, FileText } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import {
  monthsToReport,
  quarterLabel,
  quartersToReport,
  reportsKey,
  todayIn,
  useReports,
  useReportTypes,
  weeksToReport,
  type ReportRow,
} from '@/lib/reports';
import type { Program } from '@/lib/types';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/field';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

export function ReportStatus({ r }: { r: Pick<ReportRow, 'status' | 'current' | 'version' | 'correctsId'> }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {r.status === 'DRAFT' ? <Badge tone="warn">Draft</Badge> : <Badge tone="ok">Published</Badge>}
      {r.version > 1 && <Badge tone="info">{r.correctsId ? `Correction, v${r.version}` : `v${r.version}`}</Badge>}
      {r.status === 'PUBLISHED' && !r.current && <Badge>Superseded</Badge>}
    </span>
  );
}

const PERIODS: Record<
  string,
  { noun: string; button?: string; options: (p: Program) => Array<{ value: string; label: string }> }
> = {
  WEEKLY_STATUS: {
    noun: 'weekly',
    button: 'Generate weekly report',
    options: (p) =>
      weeksToReport(p.timezone, p.startDate).map((w, i) => ({
        value: w,
        label: i === 0 ? `This week (${fmtDate(w)})` : `Week of ${fmtDate(w)}`,
      })),
  },
  MONTHLY_EXECUTIVE: {
    noun: 'monthly',
    button: 'Generate monthly report',
    options: (p) =>
      monthsToReport(p.timezone, p.startDate).map((m, i) => ({
        value: `${m}-01`,
        label: `${i === 0 ? 'This month, ' : ''}${new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}`,
      })),
  },
  RAID_LOG: {
    noun: 'risk and issue log',
    button: 'Generate the log',
    options: (p) => [{ value: todayIn(p.timezone), label: `As of today (${fmtDate(todayIn(p.timezone))})` }],
  },
  BUDGET_TRACKING: {
    noun: 'budget',
    button: 'Generate budget report',
    options: (p) =>
      monthsToReport(p.timezone, p.startDate).map((m, i) => ({
        value: `${m}-01`,
        label: `${i === 0 ? 'This month, ' : ''}${new Date(`${m}-01T12:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })}`,
      })),
  },
  QUARTERLY_BOARD: {
    noun: 'board',
    button: 'Generate board materials',
    options: (p) =>
      quartersToReport(p.timezone, p.startDate).map((q, i) => ({
        value: q,
        label: `${i === 0 ? 'This quarter, ' : ''}${quarterLabel(q)}`,
      })),
  },
};

function Generate({ program, type }: { program: Program; type: string }) {
  const spec = PERIODS[type];
  const options = spec ? spec.options(program) : [];
  const [period, setPeriod] = useState(options[0]?.value ?? '');
  const qc = useQueryClient();
  const nav = useNavigate();
  const toast = useToast();
  const gen = useMutation({
    mutationFn: () =>
      api.post<{ id: string; created: boolean }>(`/api/programs/${program.id}/reports`, { type, periodStart: period }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: reportsKey(program.id) });
      toast.ok(r.created ? 'Draft generated.' : 'Draft refreshed with the latest data.');
      nav(r.id);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!spec || !options.length) return null;
  return (
    <div className="ml-auto flex items-center gap-2">
      <Select
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
        aria-label={`${spec.noun} period`}
        className="w-56"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
      <Button size="sm" onClick={() => gen.mutate()} loading={gen.isPending}>
        <FilePlus2 /> {spec.button ?? `Generate ${spec.noun} report`}
      </Button>
    </div>
  );
}

export function ReportsPage() {
  const program = useOutletContext<Program>();
  const manages = program.capabilities.includes('reports.manage');
  const types = useReportTypes(program.id);
  const reports = useReports(program.id);

  if (types.isLoading || reports.isLoading) return <Spinner className="h-64" />;
  const rows = reports.data ?? [];
  const drafts = rows.filter((r) => r.status === 'DRAFT');

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-6">
      <PageHeader
        title="Reports"
        description="Published reports are permanent. A correction is published as a new version of the same period."
      />

      {manages && drafts.length > 0 && (
        <Card className="mb-6 p-0">
          <div className="border-b border-border px-4 py-2.5 text-sm font-medium">Drafts</div>
          <ul>
            {drafts.map((r) => (
              <ReportLine key={r.id} r={r} />
            ))}
          </ul>
        </Card>
      )}

      <div className="space-y-6">
        {(types.data ?? []).map((t) => {
          const published = rows.filter((r) => r.type === t.key && r.status === 'PUBLISHED');
          return (
            <section key={t.key}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-semibold">{t.name}</h2>
                <span className="text-xs text-muted">
                  {t.cadence.toLowerCase().replace('_', ' ')}
                  {t.sourceRef ? ` · ${t.sourceRef}` : ''}
                </span>
                {manages && t.built && <Generate program={program} type={t.key} />}
              </div>
              <Card className="p-0">
                {!t.built ? (
                  <p className="px-4 py-3 text-sm text-muted">Arrives in a later sprint.</p>
                ) : published.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-muted">Nothing published yet.</p>
                ) : (
                  <ul>
                    {published.map((r) => (
                      <ReportLine key={r.id} r={r} />
                    ))}
                  </ul>
                )}
              </Card>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ReportLine({ r }: { r: ReportRow }) {
  return (
    <li className="border-b border-border last:border-0">
      <Link to={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm hover:bg-surface-2/60">
        <FileText className="size-4 text-muted" />
        <span className={r.current ? 'font-medium' : 'text-muted'}>
          {r.label ?? `Week of ${fmtDate(r.periodStart)}`}
        </span>
        <ReportStatus r={r} />
        <span className="ml-auto text-xs text-muted">
          {r.status === 'PUBLISHED'
            ? `Published ${fmtDateTime(r.publishedAt)}${r.publishedBy ? ` by ${r.publishedBy}` : ''}`
            : `Updated ${fmtDateTime(r.updatedAt)}`}
        </span>
      </Link>
    </li>
  );
}
